import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useEffect, useMemo, useState } from "react";
import SendIcon from "../../svg/SendIcon";
import {
  getUnsubscribeSecretToken,
  getUserEmailPreferences,
  updateUserEmailPreferences } from "../../utils/api";
import LoadingPage from "../LoadingPage";
import ParticipationSettings from "./participationSettings";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import EmailPreferences from "./EmailPreferences";
import AccountSettings from "./AccountSettings";
import { emailTypes } from "./EmailPreferences";
import { useTheme } from "@mui/material/styles";

const ProfileSettings: React.FC = () => {
    const { authUser, authLoading } = useAuth();
    const [unsubscribeAll, setUnsubscribeAll] = useState<boolean>(false)
    const [preferences, setPreferences] = useState<string[]>([])
    const [oldPreferences, setOldPreferences] = useState<string[]>([])
    const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false)
    const [emailPreferenceFeedback, setEmailPreferenceFeedback] = useState<string | null>(null)
    const [email, setEmail] = useState<string>('')
    const [manualFeedback, setManualFeedback] = useState<string>("")

    const [searchParams] = useSearchParams();
    const secretIdFromUrl = searchParams.get("s"); // coming from /unsubscribe?s=...
    const [secretId, setSecretId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const theme = useTheme();

    type EmailType = typeof emailTypes[number]['type']

    type EmailPrefsMap = Record<EmailType, boolean> & {unsubscribeAll?:boolean}

    const mapToArray = (map:EmailPrefsMap): EmailType[] =>
        emailTypes.map(e => e.type).filter(e => !!map[e]);

   useEffect(() => {
    const init = async () => {
        try {
            if (secretIdFromUrl) {
                setSecretId(secretIdFromUrl);
            }
            else if (authUser?.uid) {
                setEmail(authUser.email ?? '');
                const resp = await getUnsubscribeSecretToken(authUser.uid, authUser.token || "")
                if (resp.ok) {
                    const data = await resp.json();
                    setSecretId(data.secret_id)
                } else {
                    // Failed to get secret token, stop loading
                    setLoading(false);
                }
            } else if (!authLoading) {
                // No secretIdFromUrl and no authUser, stop loading
                setLoading(false);
            }
        } catch (err) {
            console.error("Failed to get secret_id", err)
            setLoading(false);  // Stop loading on error
        }
    }
    init();
}, [authUser, authLoading, secretIdFromUrl])

useEffect(() => {
    const fetchEmailPreferences = async () => {
        if(!secretId) {
            return;  // Don't set loading false here - let init() handle it
        }
        try {
            const resp = await getUserEmailPreferences(secretId)
            if (resp.ok) {
                const data = await resp.json()
                setEmail(data?.email ?? "")
                const map: EmailPrefsMap = data?.emailPreferences ?? data ?? {}
                const enabled = mapToArray(map)
                const unsub = Boolean(map.unsubscribeAll)
                setPreferences(enabled);
                setUnsubscribeAll(unsub);
                setOldPreferences(unsub ? [] : enabled);
            }
        }
        finally{
            setLoading(false)
        }
    }

    fetchEmailPreferences()

}, [secretId])

    const feedbackOptions = [
        "I get too many emails",
        "Content isn't relevant",
        "I didn't sign up for these"
    ]


    const handleEmailPreferenceFeebackSubmit= () => {
        if (emailPreferenceFeedback === 'Other') {
            setManualFeedback("")
        }
        setEmailPreferenceFeedback(null)
        setShowFeedbackModal(false)
    }

    const isSaveDisabled = useMemo(() => {
        if (loading) return true;
        if (unsubscribeAll) {
        // Save should only enable if user is *switching* to unsubscribeAll from any previous non-empty prefs
            return oldPreferences.length === 0;
        }
        const areEqual =
            preferences.length === oldPreferences.length &&
            preferences.every((p) => oldPreferences.includes(p));
        return areEqual;
    }, [loading, unsubscribeAll, preferences, oldPreferences]);

    // if not logged in and no secret id
    if (!secretIdFromUrl && (!authLoading && !authUser?.uid)) {
        // redirect to creators page
        return <Navigate to="/creators" />
    }

    const handleSavePreferences = async () => {
        if (!secretId) return;

        const prefMap: Record<string, boolean> = {};

        emailTypes.forEach((email) => {
            prefMap[email.type] = preferences.includes(email.type);
        });
        prefMap.unsubscribeAll = unsubscribeAll;

        try {
            await updateUserEmailPreferences(secretId, prefMap);
            if (unsubscribeAll) {
                setOldPreferences([])
                setShowFeedbackModal(true);
            }
            else {
                setOldPreferences(preferences);
            }
        } catch (err) {
            console.error("Failed to update preferences", err);
        }
    }

    const handleCheckPreference = (type: string) => {
        setPreferences(prev =>
            prev.includes(type)
                ? prev.filter(p => p !== type)
                : [...prev, type]
        );
    }

    const handleCloseFeedbackModal = () => {
        setShowFeedbackModal(false)
        setEmailPreferenceFeedback(null)
        setManualFeedback("")
    }

    return(
        <>
            {loading ?
                <LoadingPage />
                :
                <Box
                    sx={{
                        marginBottom: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Box sx={{
                        p: 3,
                        py: 1.5,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        borderBottom: `2px solid ${theme.palette.divider}`
                    }}>
                        <Typography variant="h6">
                            Settings
                        </Typography>
                        <Button
                            disabled={isSaveDisabled}
                            onClick={handleSavePreferences}
                            variant='contained'
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 1,
                                backgroundColor: theme.palette.primary.main,
                            }}
                        >
                            {/* <Icon><FloppyDiskIcon color={isSaveDisabled ? theme.palette.action.imageHoverDisabledText : theme.palette.primary.contrastText}/></Icon> */}
                            <Typography variant="body1">Save</Typography>
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            width: '100%',
                            maxWidth: 'lg',
                        }}
                    >
                        <EmailPreferences
                            email={email}
                            preferences={preferences}
                            unsubscribeAll={unsubscribeAll}
                            handleCheckPreference={handleCheckPreference}
                            setUnsubscribeAll={setUnsubscribeAll}
                        />
                        <ParticipationSettings />
                        <AccountSettings />
                    </Box>
                    <Modal open={showFeedbackModal} onClose={handleCloseFeedbackModal}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                p: 4,
                                borderRadius: 2,
                                minWidth: 300,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                alignItems: 'start'
                            }}
                        >
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="h5">You have unsubscribed!</Typography>
                                <Typography variant="subtitle1">Your feedback is important so we can better serve you.</Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'start',
                                    border: `2px solid ${theme.palette.divider}`,
                                    borderRadius: 2.5,
                                    width: '100%'
                                }}
                            >
                                {feedbackOptions.map((feedback, index) => (
                                    <Box key={feedback} sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mb: 1,
                                            paddingY: 1,
                                            borderBottom: `2px solid ${theme.palette.divider}`,
                                            width: '100%',
                                        }}>
                                        <Checkbox
                                            name="feedback"
                                            value={feedback}
                                            checked={emailPreferenceFeedback === feedback}
                                            onChange={() => setEmailPreferenceFeedback(feedback)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        <Typography variant="body1">{feedback}</Typography>
                                    </Box>
                                ))}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 1, mb: 1,
                                            paddingY: 1,}}
                                >
                                    <Box sx={{display:'flex', flexDirection: 'row', alignItems: 'center', justifyContent:'center'}}>
                                        <Checkbox
                                            name="feedback"
                                            value="Other"
                                            checked={emailPreferenceFeedback === "Other"}
                                            onChange={() => setEmailPreferenceFeedback("Other")}
                                            style={{ marginRight: '8px' }}
                                        />
                                        <Typography variant="body1">Other</Typography>
                                    </Box>
                                    <TextField
                                        sx={{ml: 10, mb:1, width: '100%'}}
                                        value={manualFeedback}
                                        onChange={(e) => setManualFeedback(e.target.value)}
                                        label="Reason for unsubscribing"
                                        variant="outlined"
                                        onClick={() => setEmailPreferenceFeedback("Other")}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'end',
                                gap: 1,
                            }}>
                                <Button onClick={handleCloseFeedbackModal} variant="outlined">
                                    Skip
                                </Button>
                                <Button onClick={handleEmailPreferenceFeebackSubmit}
                                    sx={{
                                        backgroundColor: theme.palette.primary.main
                                    }}
                                    variant="contained"
                                    disabled={!emailPreferenceFeedback || (emailPreferenceFeedback === 'Other' &&  !manualFeedback.trim())}
                                >
                                    <Box sx={{display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'center', alignItems: 'center'}}>
                                        <SendIcon color={
                                            !emailPreferenceFeedback || (emailPreferenceFeedback === 'Other' &&  !manualFeedback.trim())
                                            ? theme.palette.action.imageHoverDisabledText : theme.palette.primary.contrastText
                                        }/>
                                        <Typography>Submit</Typography>
                                    </Box>
                                </Button>
                            </Box>
                        </Box>
                    </Modal>
                </Box>
            }
        </>
    )
}

export default ProfileSettings
